import { MediaLibraryConsts, MediaLibrarySize, MediaLibraryType } from '@shared/MediaLibraryConsts';
import { CompilerOptions, NgModuleRef, Type } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { AppAuthService } from '@app/shared/common/auth/app-auth.service';
import { AppConsts } from '@shared/AppConsts';
import { SubdomainTenancyNameFinder } from '@shared/helpers/SubdomainTenancyNameFinder';
import { UrlHelper } from './shared/helpers/UrlHelper';
import { XmlHttpRequestHelper } from '@shared/helpers/XmlHttpRequestHelper';
import { DynamicResourcesHelper } from '@shared/helpers/DynamicResourcesHelper';
import { environment } from './environments/environment';
import { LocaleMappingService } from '@shared/locale-mapping.service';
import { LocalStorageService } from '@shared/utils/local-storage.service';
import { merge as _merge } from 'lodash-es';
import { DateTime, Settings } from 'luxon';
import {
    AccountServiceProxy,
    IsTenantAvailableInput,
    IsTenantAvailableOutput,
    TenantAvailabilityState,
    TokenAuthServiceProxy,
} from '@shared/service-proxies/service-proxies';
import { Injector } from '@angular/core';
import { TokenService } from 'abp-ng2-module';

export class AppPreBootstrap {
    static run(appRootUrl: string, injector: Injector, callback: () => void, resolve: any, reject: any): void {
        AppPreBootstrap.getApplicationConfig(appRootUrl, injector, () => {
            if (UrlHelper.isInstallUrl(location.href)) {
                AppPreBootstrap.loadAssetsForInstallPage(callback);
                return;
            }

            const queryStringObj = UrlHelper.getQueryParameters();
            if (queryStringObj.redirect && queryStringObj.redirect === 'TenantRegistration') {
                if (queryStringObj.forceNewRegistration) {
                    new AppAuthService().logout();

                }

                location.href = AppConsts.appBaseUrl + '/account/select-edition';
            } else if (queryStringObj.impersonationToken) {
                if (queryStringObj.userDelegationId) {
                    AppPreBootstrap.delegatedImpersonatedAuthenticate(
                        queryStringObj.userDelegationId,
                        queryStringObj.impersonationToken,
                        queryStringObj.tenantId,
                        () => {
                            AppPreBootstrap.getUserConfiguration(callback);
                        }
                    );
                } else {
                    AppPreBootstrap.impersonatedAuthenticate(
                        queryStringObj.impersonationToken,
                        queryStringObj.tenantId,
                        () => {
                            AppPreBootstrap.getUserConfiguration(callback);
                        }
                    );
                }
            } else if (queryStringObj.switchAccountToken) {
                AppPreBootstrap.linkedAccountAuthenticate(
                    queryStringObj.switchAccountToken,
                    queryStringObj.tenantId,
                    () => {
                        AppPreBootstrap.getUserConfiguration(callback);
                    }
                );
            } else {
                AppPreBootstrap.getUserConfiguration(callback);
            }
        });
    }

    static bootstrap<TM>(
        moduleType: Type<TM>,
        compilerOptions?: CompilerOptions | CompilerOptions[]
    ): Promise<NgModuleRef<TM>> {
        return platformBrowserDynamic().bootstrapModule(moduleType, compilerOptions);
    }

    private static getApplicationConfig(appRootUrl: string, injector: Injector, callback: () => void) {
        let type = 'GET';
        let url = appRootUrl + 'assets/' + environment.appConfig;
        let customHeaders = [
            {
                name: abp.multiTenancy.tenantIdCookieName,
                value: abp.multiTenancy.getTenantIdCookie() + '',
            },
        ];

        XmlHttpRequestHelper.ajax(type, url, customHeaders, null, (result) => {
            const subdomainTenancyNameFinder = new SubdomainTenancyNameFinder();
            const tenancyName = subdomainTenancyNameFinder.getCurrentTenancyNameOrNull(result.appBaseUrl);

            AppConsts.localeMappings = result.localeMappings;

            AppConsts.appBaseUrlFormat = result.appBaseUrl;
            AppConsts.remoteServiceBaseUrlFormat = result.remoteServiceBaseUrl;
            AppConsts.powerBiUrl = result.powerBiUrl;
            AppConsts.powerBiReturnUrl = result.powerBiReturnUrl;
            AppConsts.recaptchaSiteKey = result.recaptchaSiteKey;
            AppConsts.clientHeaderName = result.clientHeaderName;
            AppConsts.clientHeaderValue = result.clientHeaderValue;
            if (tenancyName == null) {
                AppConsts.appBaseUrl = result.appBaseUrl.replace(AppConsts.tenancyNamePlaceHolderInUrl + '.', '');
                AppConsts.remoteServiceBaseUrl = result.remoteServiceBaseUrl.replace(
                    AppConsts.tenancyNamePlaceHolderInUrl + '.',
                    ''
                );

                callback();
            } else {
                AppConsts.appBaseUrl = result.appBaseUrl.replace(AppConsts.tenancyNamePlaceHolderInUrl, tenancyName);
                AppConsts.remoteServiceBaseUrl = result.remoteServiceBaseUrl.replace(
                    AppConsts.tenancyNamePlaceHolderInUrl,
                    tenancyName
                );

                if (!subdomainTenancyNameFinder.urlHasTenancyNamePlaceholder(result.remoteServiceBaseUrl)) {
                    let accountServiceProxy: AccountServiceProxy = injector.get(AccountServiceProxy);
                    let input = new IsTenantAvailableInput();
                    input.tenancyName = tenancyName;

                    accountServiceProxy.isTenantAvailable(input).subscribe((result: IsTenantAvailableOutput) => {
                        if (result.state === TenantAvailabilityState.Available) {
                            abp.multiTenancy.setTenantIdCookie(result.tenantId);
                        }

                        callback();
                    });
                } else {
                    callback();
                }
            }
        });
    }

    private static getCurrentClockProvider(currentProviderName: string): abp.timing.IClockProvider {
        if (currentProviderName === 'unspecifiedClockProvider') {
            return abp.timing.unspecifiedClockProvider;
        }

        if (currentProviderName === 'utcClockProvider') {
            return abp.timing.utcClockProvider;
        }

        return abp.timing.localClockProvider;
    }

    private static getRequetHeadersWithDefaultValues() {
        const cookieLangValue = abp.utils.getCookieValue('Abp.Localization.CultureName');

        let requestHeaders = {
            '.AspNetCore.Culture': 'c=' + cookieLangValue + '|uic=' + cookieLangValue,
            [abp.multiTenancy.tenantIdCookieName]: abp.multiTenancy.getTenantIdCookie(),
        };

        if (!cookieLangValue) {
            delete requestHeaders['.AspNetCore.Culture'];
        }

        return requestHeaders;
    }

    private static impersonatedAuthenticate(impersonationToken: string, tenantId: number, callback: () => void): void {
        abp.multiTenancy.setTenantIdCookie(tenantId);
        let requestHeaders = AppPreBootstrap.getRequetHeadersWithDefaultValues();

        XmlHttpRequestHelper.ajax(
            'POST',
            AppConsts.remoteServiceBaseUrl +
            '/api/TokenAuth/ImpersonatedAuthenticate?impersonationToken=' +
            impersonationToken,
            requestHeaders,
            null,
            (response) => {
                let result = response.result;
                abp.auth.setToken(result.accessToken);
                AppPreBootstrap.setEncryptedTokenCookie(result.encryptedAccessToken, () => {
                    callback();
                    location.search = '';
                });
            }
        );
    }

    private static delegatedImpersonatedAuthenticate(
        userDelegationId: number,
        impersonationToken: string,
        tenantId: number,
        callback: () => void
    ): void {
        abp.multiTenancy.setTenantIdCookie(tenantId);
        let requestHeaders = AppPreBootstrap.getRequetHeadersWithDefaultValues();

        XmlHttpRequestHelper.ajax(
            'POST',
            AppConsts.remoteServiceBaseUrl +
            '/api/TokenAuth/DelegatedImpersonatedAuthenticate?userDelegationId=' +
            userDelegationId +
            '&impersonationToken=' +
            impersonationToken,
            requestHeaders,
            null,
            (response) => {
                let result = response.result;
                abp.auth.setToken(result.accessToken);

                AppPreBootstrap.setEncryptedTokenCookie(result.encryptedAccessToken, () => {
                    callback();
                    location.search = '';
                });
            }
        );
    }

    private static linkedAccountAuthenticate(switchAccountToken: string, tenantId: number, callback: () => void): void {
        abp.multiTenancy.setTenantIdCookie(tenantId);
        let requestHeaders = AppPreBootstrap.getRequetHeadersWithDefaultValues();

        XmlHttpRequestHelper.ajax(
            'POST',
            AppConsts.remoteServiceBaseUrl +
            '/api/TokenAuth/LinkedAccountAuthenticate?switchAccountToken=' +
            switchAccountToken,
            requestHeaders,
            null,
            (response) => {
                let result = response.result;
                abp.auth.setToken(result.accessToken);
                AppPreBootstrap.setEncryptedTokenCookie(result.encryptedAccessToken, () => {
                    callback();
                    location.search = '';
                });
            }
        );
    }

    private static isTokenExpired(token: string): boolean {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.exp < Date.now() / 1000;
        } catch {
            return true;
        }
    }

    private static loadAnonymousConfiguration(callback: () => void): void {
        const cookieLang = abp.utils.getCookieValue('Abp.Localization.CultureName') || 'ar';
        if (!abp.localization.currentLanguage?.name) {
            abp.localization.currentLanguage = { name: cookieLang, displayName: cookieLang, icon: '', isDefault: true, isDisabled: false, isRightToLeft: cookieLang === 'ar' };
        }
        if (!abp.timing.timeZoneInfo?.iana?.timeZoneId) {
            abp.timing.timeZoneInfo = { iana: { timeZoneId: 'UTC' }, windows: { timeZoneId: 'UTC', baseUtcOffsetInMilliseconds: 0, currentUtcOffsetInMilliseconds: 0, isDaylightSavingTimeNow: false } };
        }
        if (!abp.setting.values['App.UiManagement.Theme']) {
            abp.setting.values['App.UiManagement.Theme'] = 'theme2';
            abp.setting.values['default.App.UiManagement.ThemeColor'] = 'light';
        }
        abp.clock.provider = abp.timing.utcClockProvider;

        const publicLocUrl = AppConsts.remoteServiceBaseUrl + '/api/public/localization/getAll';
        const publicLocHeaders = [{ name: 'Accept-Language', value: cookieLang }];

        const loadLocalization = () => {
            XmlHttpRequestHelper.ajax('GET', publicLocUrl, publicLocHeaders, null, (result) => {
                if (result) {
                    if (!abp.localization.values['EEP']) {
                        abp.localization.values['EEP'] = {};
                    }
                    Object.assign(abp.localization.values['EEP'], result.result);
                }
                abp.event.trigger('abp.dynamicScriptsInitialized');
                DynamicResourcesHelper.loadResources(callback);
            });
        };

        // Anonymous visitors get no user configuration, so fetch the Nafath switch separately.
        // If the call fails, keep Nafath as the default login method.
        const nafathSettingName = 'App.UserManagement.IsNafathLoginEnabled';
        XmlHttpRequestHelper.ajax(
            'GET',
            AppConsts.remoteServiceBaseUrl + '/api/public/auth-settings/getAll',
            {},
            null,
            (result) => {
                abp.setting.values[nafathSettingName] = String(result?.result?.isNafathLoginEnabled !== false);
                loadLocalization();
            },
            false,
            () => {
                abp.setting.values[nafathSettingName] = 'true';
                loadLocalization();
            }
        );
    }

    private static getUserConfiguration(callback: () => void): any {
        const token = abp.auth.getToken();

        if (!token || AppPreBootstrap.isTokenExpired(token)) {
            AppPreBootstrap.loadAnonymousConfiguration(callback);
            return;
        }

        let requestHeaders = AppPreBootstrap.getRequetHeadersWithDefaultValues();

        requestHeaders['Authorization'] = 'Bearer ' + token;
        requestHeaders[AppConsts.clientHeaderName] = AppConsts.clientHeaderValue;

        return XmlHttpRequestHelper.ajax(
            'GET',
            AppConsts.remoteServiceBaseUrl + '/AbpUserConfiguration/GetAll',
            requestHeaders,
            null,
            (response) => {
                let result = response.result;

                _merge(abp, result);

                abp.clock.provider = this.getCurrentClockProvider(result.clock.provider);

                AppPreBootstrap.configureLuxon();

                abp.event.trigger('abp.dynamicScriptsInitialized');
                //AppConsts.recaptchaSiteKey = abp.setting.get('Recaptcha.SiteKey');
                AppConsts.subscriptionExpireNootifyDayCount = parseInt(
                    abp.setting.get('App.TenantManagement.SubscriptionExpireNotifyDayCount')
                );

                //Media Library pass allowed types and size limitation from backend.
                let types = Object.entries(JSON.parse(abp.setting.get('MediaLibrarySettings.AllowedUploadTypes')));
                for (let index = 0; index < types.length; index++) {
                    let mediatype = new MediaLibraryType();
                    mediatype.Type = types[index][0];
                    mediatype.Mime = types[index][1].toString();
                    MediaLibraryConsts.AllowUploadTypes.push(mediatype);
                }
                let sizes = Object.entries(JSON.parse(abp.setting.get('MediaLibrarySettings.MaxSizeLimitInByte')));
                for (let index = 0; index < sizes.length; index++) {
                    let mediasize = new MediaLibrarySize();
                    mediasize.Mime = sizes[index][0];
                    mediasize.MaxSize = parseInt(sizes[index][1].toString());
                    MediaLibraryConsts.MaxSizeLimitInByte.push(mediasize);
                }
                MediaLibraryConsts.AllowUploadtypesLabel = MediaLibraryConsts.AllowUploadTypes.map((item, index) => {
                    return item.Type;
                }).join(',');

                MediaLibraryConsts.GeneralSizeLimit = MediaLibraryConsts.MaxSizeLimitInByte.find((size) => {
                    return size.Mime == "*";
                }).MaxSize;
                //
                //contactUs Map

                AppConsts.useMapInContactUs = abp.setting.getBoolean('UseMapInContactUs');

                AppConsts.nonceEnabled = abp.setting.getBoolean('CPS.EnableNonce');
                
                AppConsts.httpOnlySetting = abp.setting.getBoolean('Auth.HttpOnly');
                DynamicResourcesHelper.loadResources(callback);
            },
            false,
            (status: number) => {
                if (status === 401) {
                    // Token was stored but server rejected it (revoked session, logout from another browser, etc.)
                    // Clear the stale token and proceed as anonymous.
                    abp.auth.clearToken();
                    AppPreBootstrap.loadAnonymousConfiguration(callback);
                } else {
                    alert(abp.localization.localize('InternalServerError', 'AbpWeb'));
                }
            }
        );
    }

    private static configureLuxon() {
        let luxonLocale = new LocaleMappingService().map('luxon', abp.localization.currentLanguage.name);

        DateTime.local().setLocale(luxonLocale);
        DateTime.utc().setLocale(luxonLocale);
        Settings.defaultLocale = luxonLocale;

        if (abp.clock.provider.supportsMultipleTimezone) {
            Settings.defaultZoneName = abp.timing.timeZoneInfo.iana.timeZoneId;
        }

        Date.prototype.toISOString = function () {
            let value = DateTime.fromJSDate(this)
                .setLocale('en')
                .setZone(abp.timing.timeZoneInfo.iana.timeZoneId)
                .toString();
            return value;
        };

        Date.prototype.toString = function () {
            let value = DateTime.fromJSDate(this)
                .setLocale('en')
                .setZone(abp.timing.timeZoneInfo.iana.timeZoneId)
                .toString();
            return value;
        };

        DateTime.prototype.toString = function () {
            let date = this.setLocale('en').setZone(abp.timing.timeZoneInfo.iana.timeZoneId) as DateTime;
            return date.toISO();
        };
    }

    private static setEncryptedTokenCookie(encryptedToken: string, callback: () => void) {
        new LocalStorageService().setItem(
            AppConsts.authorization.encrptedAuthTokenName,
            {
                token: encryptedToken,
                expireDate: new Date(new Date().getTime() + 365 * 86400000), //1 year
            },
            callback
        );
    }

    private static loadAssetsForInstallPage(callback) {
        abp.setting.values['App.UiManagement.Theme'] = 'default';
        abp.setting.values['default.App.UiManagement.ThemeColor'] = 'default';

        DynamicResourcesHelper.loadResources(callback);
    }
}
